import React, { useState, useEffect } from 'react';
import { 
  AppBar, Box, Toolbar, Typography, IconButton, 
  Menu, MenuItem, Avatar, Tooltip, ListItemIcon, Divider, Drawer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
  CircularProgress, Alert,
} from '@mui/material';
import { Logout, Person, Settings, Lock, DeleteForever, WarningAmber } from '@mui/icons-material';
import { useNavigate, Outlet } from 'react-router-dom';
import Sidebar from './Sidebar'; // Your existing sidebar component
import { authService } from '../services/authService';
import { api } from '../services/api';
import { ROLES } from '../constants/roles';
import { useNotification } from '../contexts/NotificationContext';

const Layout = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  
  // State for the Profile Menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [user, setUser] = useState<{ name?: string; role?: string } | null>(null);

  // Erase All Dialog State
  const [eraseDialogOpen, setEraseDialogOpen] = useState(false);
  const [eraseStep, setEraseStep] = useState<'first' | 'confirm'>('first');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [eraseLoading, setEraseLoading] = useState(false);
  const [eraseError, setEraseError] = useState<string | null>(null);

  const isAdmin = user?.role === ROLES.ADMIN;

  useEffect(() => {
    const u = authService.getUser();
    if (u) setUser(u);
  }, []);

  const getInitials = () => {
    const name = user?.name || 'User';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    // logout() clears the session and performs a hard reload to /login
    authService.logout();
  };

  // Erase All Handlers
  const handleOpenEraseDialog = () => {
    setEraseDialogOpen(true);
    setEraseStep('first');
    setPassword('');
    setConfirmPassword('');
    setEraseError(null);
  };

  const handleCloseEraseDialog = () => {
    setEraseDialogOpen(false);
    setPassword('');
    setConfirmPassword('');
    setEraseError(null);
    setEraseStep('first');
  };

  const handleEraseFirstStep = () => {
    if (!password) {
      setEraseError('Please enter your password');
      return;
    }
    setEraseError(null);
    setEraseStep('confirm');
  };

  const handleEraseConfirm = async () => {
    if (!confirmPassword) {
      setEraseError('Please confirm your password');
      return;
    }
    if (password !== confirmPassword) {
      setEraseError('Passwords do not match');
      return;
    }

    setEraseLoading(true);
    setEraseError(null);

    try {
      // Step 1: Verify password using auth API
      const userEmail = authService.getUser()?.email;
      if (!userEmail) {
        setEraseError('User session not found. Please login again.');
        setEraseLoading(false);
        return;
      }

      await api.post('/api/auth/verify-password', { password });

      // Step 2: Call erase all API
      await api.post('/api/admin/erase-all', { password });

      showSuccess('All data has been erased successfully');
      handleCloseEraseDialog();
      
      // Logout after erase (clears session and hard reloads to /login)
      authService.logout();
    } catch (err: any) {
      setEraseError(err.message|| 'Verification failed. Please check your password.');
    } finally {
      setEraseLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* HEADER BAR */}
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          
           {/* LEFT SIDE: Title */}
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
            Inventory Management
          </Typography>
          
          {/* RIGHT SIDE: Profile Icon with Hover/Click Menu */}
          <Box>
            <Tooltip title="Account settings">
              <IconButton
                onClick={handleOpenMenu}
                size="small"
                sx={{ ml: 2 }}
                aria-controls={open ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
              >
                <Avatar sx={{ width: 35, height: 35, bgcolor: 'secondary.main' }}>
                  {getInitials()}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>

        </Toolbar>
      </AppBar>

      {/* Profile Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleCloseMenu}
        onClick={handleCloseMenu}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        PaperProps={{
          elevation: 3,
          sx: { mt: 1.5, minWidth: 180 }
        }}
      >
        <MenuItem onClick={() => navigate('/profile')}>
          <ListItemIcon><Person fontSize="small" /></ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={() => navigate('/change-password')}>
          <ListItemIcon><Lock fontSize="small" /></ListItemIcon>
          Change Password
        </MenuItem>
        {isAdmin && (
          <>
            <Divider />
            <MenuItem onClick={handleOpenEraseDialog} sx={{ color: 'error.main' }}>
              <ListItemIcon><DeleteForever fontSize="small" color="error" /></ListItemIcon>
              Erase All Data
            </MenuItem>
          </>
        )}
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <ListItemIcon><Logout fontSize="small" color="error" /></ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Erase All Confirmation Dialog */}
      <Dialog
        open={eraseDialogOpen}
        onClose={handleCloseEraseDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <WarningAmber sx={{ color: 'error.main', fontSize: 28 }} />
            <Typography variant="h6" fontWeight={600} color="error.main">
              {eraseStep === 'first' ? 'Erase All Data' : 'Confirm Erase All'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {eraseStep === 'first' ? (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                <strong>Warning:</strong> This action will permanently delete ALL data including customers, suppliers, items, orders, and inventory. This cannot be undone!
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please enter your password to proceed:
              </Typography>
              <TextField
                fullWidth
                type="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                error={!!eraseError}
                helperText={eraseError}
              />
            </>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are about to erase <strong>ALL DATA</strong>. Enter your password again to confirm.
              </Alert>
              <TextField
                fullWidth
                type="password"
                label="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoFocus
                error={!!eraseError}
                helperText={eraseError}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleCloseEraseDialog}
            variant="outlined"
            disabled={eraseLoading}
          >
            Cancel
          </Button>
          {eraseStep === 'first' ? (
            <Button
              onClick={handleEraseFirstStep}
              variant="contained"
              color="error"
            >
              Continue
            </Button>
          ) : (
            <Button
              onClick={handleEraseConfirm}
              variant="contained"
              color="error"
              disabled={eraseLoading}
              startIcon={eraseLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteForever />}
            >
              {eraseLoading ? 'Erasing...' : 'Erase All Data'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Sidebar Component with Drawer for proper spacing */}
      <Drawer
        variant="permanent"
        sx={{
          width: 240,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: 240, boxSizing: 'border-box' },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <Sidebar />
        </Box>
      </Drawer>

      {/* MAIN CONTENT AREA */}
      <Box component="main" sx={{ flexGrow: 1, p: 2, width: '100%' }}>
        <Toolbar /> {/* This empty Toolbar acts as a spacer so content isn't hidden under the header */}
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;